import { CheckCircle2, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shared ATO benchmark range-band row. Single visual used by BOTH the client
 * app "How you compare" block and the firm-side ATO Compliance Checker, so the
 * two never diverge.
 *
 *   "Total expenses / sales            In range"  ← green / red / muted pill
 *   ├──────[ 50% ────────── 66% ]──────────┤      ← scale 0..max
 *           min          max         •58% (you)
 *   typical range 50-66% · you 58%
 */
export interface BenchmarkBarRow {
  label: string;
  yourPct: number;
  range: { min: number; max: number };
  higherIsBetter: boolean;
  verdict: 'in-range' | 'above-range' | 'below-range' | 'no-data';
}

function rangeLeftPct(range: { min: number; max: number }, scaleMax: number): number {
  return Math.max(0, Math.min(100, (range.min / scaleMax) * 100));
}
function rangeWidthPct(range: { min: number; max: number }, scaleMax: number): number {
  return Math.max(0, Math.min(100 - rangeLeftPct(range, scaleMax), ((range.max - range.min) / scaleMax) * 100));
}

export function BenchmarkRangeBar({ row }: { row: BenchmarkBarRow }) {
  if (row.verdict === 'no-data') {
    const scaleMax = Math.max(row.range.max * 1.4, 30);
    return (
      <div>
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-medium">{row.label}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md text-muted-foreground bg-muted">
            No data yet
          </span>
        </div>

        <div className="relative h-5">
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-muted" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-3 rounded-full border border-muted-foreground/25"
            style={{
              left: `${rangeLeftPct(row.range, scaleMax)}%`,
              width: `${rangeWidthPct(row.range, scaleMax)}%`,
              background: 'rgb(148 163 184 / 0.18)',
            }}
          />
        </div>

        <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
          <span>Typical range <span className="text-foreground font-semibold">{row.range.min}-{row.range.max}%</span></span>
          <span>You <span className="font-semibold tabular-nums">-</span></span>
        </div>
      </div>
    );
  }

  const isGood =
    row.verdict === 'in-range' ||
    (row.verdict === 'above-range' &&  row.higherIsBetter) ||
    (row.verdict === 'below-range' && !row.higherIsBetter);

  const verdictLabel =
    row.verdict === 'in-range'    ? 'In range' :
    row.verdict === 'above-range' ? `${row.higherIsBetter ? 'Above' : 'Higher than'} range` :
                                    `${row.higherIsBetter ? 'Below' : 'Lower than'} range`;
  const VerdictIcon =
    row.verdict === 'in-range' ? CheckCircle2 :
    isGood ? TrendingUp : TrendingDown;

  const scaleMax = Math.max(row.range.max * 1.4, row.yourPct * 1.15, 30);
  const youLeft = Math.min(100, (row.yourPct / scaleMax) * 100);

  const goodColour = 'rgb(16 185 129)';   // emerald-500
  const badColour  = 'rgb(244 63 94)';    // rose-500
  const youColour  = isGood ? goodColour : badColour;
  const rangeColour = isGood ? 'rgb(16 185 129 / 0.18)' : 'rgb(244 63 94 / 0.14)';

  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-2">
        <span className="font-medium">{row.label}</span>
        <span
          className={cn(
            'text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1 px-1.5 py-0.5 rounded-md',
            isGood
              ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40'
              : 'text-rose-700    bg-rose-50    dark:text-rose-300    dark:bg-rose-950/40',
          )}
        >
          <VerdictIcon className="w-2.5 h-2.5" />
          {verdictLabel}
        </span>
      </div>

      <div className="relative h-5">
        {/* baseline track */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1 rounded-full bg-muted" />
        {/* the industry range band */}
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3 rounded-full border"
          style={{
            left: `${rangeLeftPct(row.range, scaleMax)}%`,
            width: `${rangeWidthPct(row.range, scaleMax)}%`,
            background: rangeColour,
            borderColor: isGood ? 'rgb(16 185 129 / 0.45)' : 'rgb(244 63 94 / 0.45)',
          }}
        />
        {/* your marker */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-background shadow"
          style={{ left: `calc(${youLeft}% - 6px)`, background: youColour }}
          title={`You: ${row.yourPct.toFixed(1)}%`}
        />
      </div>

      <div className="flex items-center justify-between mt-2 text-[10px] text-muted-foreground">
        <span>Typical range <span className="text-foreground font-semibold">{row.range.min}-{row.range.max}%</span></span>
        <span>You <span className="text-foreground font-semibold tabular-nums">{row.yourPct.toFixed(1)}%</span></span>
      </div>

      {!row.higherIsBetter && (
        <p className="mt-1 text-[10px] text-muted-foreground italic">Lower is better (less of your revenue spent on this).</p>
      )}
    </div>
  );
}
